import React from "react";
import { Box, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import AutorenewRounded from "@mui/icons-material/AutorenewRounded";
import { isDarkMode } from "../../utils/constants";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const scanSweep = keyframes`
  0% { transform: translateX(-100%); opacity: 0; }
  18% { opacity: 0.4; }
  100% { transform: translateX(130%); opacity: 0; }
`;

const processCardDropIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-14px) scale(0.985);
  }
  65% {
    opacity: 1;
    transform: translateY(1px) scale(1);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const ProcessQueueCard = ({
  text,
  detail,
  done = false,
  isActive = false,
  phaseMeta,
  enterIndex = 0,
}) => {
  const isSearchPhase = phaseMeta.kind === "search";
  const isCompact = done && !isActive;
  const eyebrow = isActive ? "Current Process" : "Data Process";
  const iconBackground = done
    ? (isDarkMode ? "rgba(42, 155, 96, 0.22)" : "rgba(44, 174, 103, 0.12)")
    : (isSearchPhase
      ? (isDarkMode ? "rgba(34,211,166,0.16)" : "rgba(34,211,166,0.12)")
      : (isDarkMode ? "rgba(36,157,255,0.16)" : "rgba(36,157,255,0.12)"));
  const iconColor = done
    ? (isDarkMode ? "#B9F6CF" : "#237A48")
    : phaseMeta.accent;
  const sweepBackground = isSearchPhase
    ? "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(113,255,223,0.13) 45%, rgba(255,255,255,0) 100%)"
    : "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(133,196,255,0.12) 45%, rgba(255,255,255,0) 100%)";
  const indicatorGlow = done
    ? (isDarkMode ? "rgba(42, 155, 96, 0.18)" : "rgba(44, 174, 103, 0.12)")
    : (isSearchPhase ? "rgba(34,211,166,0.12)" : "rgba(36,157,255,0.12)");

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        mt: isCompact ? 0.65 : 1,
        mb: isCompact ? 0.75 : 1.35,
        py: isCompact ? 0.85 : 1.25,
        px: 1.1,
        borderRadius: 2,
        border: `1px solid ${phaseMeta.panelBorder}`,
        background: phaseMeta.panelBackground,
        boxShadow: isCompact ? "none" : phaseMeta.panelShadow,
        opacity: isCompact ? 0.92 : 1,
        animation: `${processCardDropIn} 420ms cubic-bezier(0.22, 1, 0.36, 1) both`,
        animationDelay: `${Math.min(enterIndex, 6) * 48}ms`,
        willChange: "transform, opacity",
        transition: "border-color 220ms ease, background 220ms ease, box-shadow 220ms ease",
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
          transition: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: sweepBackground,
          opacity: isCompact ? 0 : done ? 0.35 : 1,
          animation: isActive ? `${scanSweep} ${isSearchPhase ? 2.6 : 3.2}s ease-in-out infinite` : "none",
        }}
      />

      <Box sx={{ position: "relative", minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, minWidth: 0 }}>
          <Box
            sx={{
              width: isCompact ? 18 : 20,
              height: isCompact ? 18 : 20,
              flexShrink: 0,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: iconBackground,
              color: iconColor,
            }}
          >
            {done ? (
              <CheckRoundedIcon sx={{ fontSize: 12 }} />
            ) : isSearchPhase ? (
              <PublicRoundedIcon sx={{ fontSize: 12 }} />
            ) : (
              <AutorenewRounded
                sx={{
                  fontSize: 12,
                  animation: isActive ? `${spin} 1.2s linear infinite` : "none",
                }}
              />
            )}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {!isCompact && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0, mb: 0.55 }}>
                <Typography
                  sx={{
                    fontSize: "9.5px",
                    fontWeight: 800,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    color: phaseMeta.panelMutedColor,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  }}
                >
                  {eyebrow}
                </Typography>
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    bgcolor: iconColor,
                    boxShadow: `0 0 0 3px ${indicatorGlow}`,
                  }}
                />
              </Box>
            )}
            <Typography
              sx={{
                fontSize: isCompact ? "13px" : "14px",
                lineHeight: 1.32,
                fontWeight: 700,
                color: phaseMeta.panelTextColor,
                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                hyphens: "auto",
              }}
            >
              {text}
            </Typography>
            {detail && (
              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: isCompact ? "12px" : "12.75px",
                  lineHeight: 1.32,
                  fontWeight: 600,
                  color: phaseMeta.panelMutedColor,
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                  hyphens: "auto",
                }}
              >
                {detail}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProcessQueueCard;
